import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { getLegalDocUrl } from '../lib/legalDocs';

export const RegisterPage = () => {
  const { register } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    password: '',
    password_confirm: '',
    age: '',
    gender: '',
    location: ''
  });
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.password_confirm) {
      setError(t('كلمتا المرور غير متطابقتين',
        'Passwords do not match',
        'Les mots de passe ne correspondent pas'));
      return;
    }
    if (formData.password.length < 6) {
      setError(t('يجب أن تكون كلمة المرور 6 أحرف على الأقل',
        'Password must be at least 6 characters',
        'Le mot de passe doit comporter au moins 6 caractères'));
      return;
    }
    if (!formData.gender) {
      setError(t('يرجى اختيار الجنس', 'Please select your gender', 'Veuillez sélectionner votre sexe'));
      return;
    }
    const ageNum = parseInt(formData.age, 10);
    if (!ageNum || ageNum < 16 || ageNum > 120) {
      setError(t('يجب أن يكون العمر بين 16 و 120',
        'Age must be between 16 and 120',
        "L'âge doit être entre 16 et 120"));
      return;
    }
    if (!agreed) {
      setError(t('يجب الموافقة على الشروط والأحكام وسياسة الخصوصية للمتابعة',
        'You must agree to the Terms & Privacy Policy to continue',
        'Vous devez accepter les conditions pour continuer'));
      return;
    }
    setError('');
    setLoading(true);

    try {
      await register({ ...formData, age: ageNum });
      try { localStorage.removeItem('tababa_perm_asked'); } catch (e) { /* ignore */ }
      navigate('/permissions', { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          t('حدث خطأ في التسجيل', 'Registration failed', "Échec de l'inscription")
      );
    } finally {
      setLoading(false);
    }
  };

  const isRTL = language === 'ar';

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4 py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#0d47a1] mb-2">
            {t('إنشاء حساب جديد', 'Create New Account', 'Créer un compte')}
          </h1>
          <p className="text-gray-600">
            {t('انضم إلينا واحصل على رعاية طبية متميزة',
              'Join us for excellent healthcare',
              'Rejoignez-nous pour des soins exceptionnels')}
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6" data-testid="register-error-alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="full_name">{t('الاسم الكامل', 'Full Name', 'Nom complet')}</Label>
            <Input id="full_name" name="full_name" type="text" value={formData.full_name} onChange={handleChange}
              placeholder={t('أدخل اسمك الكامل', 'Enter your full name', 'Entrez votre nom complet')}
              required data-testid="full-name-input" className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="email">{t('البريد الإلكتروني', 'Email', 'E-mail')}</Label>
            <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange}
              placeholder={t('example@email.com', 'example@email.com', 'example@email.com')}
              required data-testid="email-input" className="mt-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('سيُستخدم لاستعادة الحساب والإشعارات',
                'Used for account recovery and notifications',
                'Pour la récupération du compte et les notifications')}
            </p>
          </div>

          <div>
            <Label htmlFor="phone_number">{t('رقم الهاتف', 'Phone Number', 'Téléphone')}</Label>
            <Input id="phone_number" name="phone_number" type="tel" value={formData.phone_number} onChange={handleChange}
              placeholder={t('أدخل رقم هاتفك', 'Enter your phone number', 'Entrez votre téléphone')}
              required data-testid="phone-input" className="mt-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('سيكون رقم هاتفك هو معرف الدخول',
                'Your phone number will be your login ID',
                'Votre numéro sera votre identifiant')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="age">{t('العمر', 'Age', 'Âge')}</Label>
              <Input id="age" name="age" type="number" min="16" max="120" value={formData.age} onChange={handleChange}
                placeholder="25" required data-testid="age-input" className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="gender">{t('الجنس', 'Gender', 'Sexe')}</Label>
              <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                <SelectTrigger id="gender" className="mt-2" data-testid="gender-select">
                  <SelectValue placeholder={t('اختر', 'Select', 'Choisir')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male" data-testid="gender-male">{t('ذكر', 'Male', 'Homme')}</SelectItem>
                  <SelectItem value="female" data-testid="gender-female">{t('أنثى', 'Female', 'Femme')}</SelectItem>
                  <SelectItem value="other" data-testid="gender-other">{t('آخر', 'Other', 'Autre')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="password">{t('كلمة المرور', 'Password', 'Mot de passe')}</Label>
            <Input id="password" name="password" type="password" value={formData.password} onChange={handleChange}
              placeholder={t('6 أحرف على الأقل', 'At least 6 characters', 'Au moins 6 caractères')}
              required data-testid="password-input" className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="password_confirm">{t('تأكيد كلمة المرور', 'Confirm Password', 'Confirmer le mot de passe')}</Label>
            <Input id="password_confirm" name="password_confirm" type="password"
              value={formData.password_confirm} onChange={handleChange}
              placeholder={t('أعد إدخال كلمة المرور', 'Re-enter your password', 'Ressaisissez le mot de passe')}
              required data-testid="password-confirm-input" className="mt-2"
            />
            {formData.password_confirm && formData.password !== formData.password_confirm && (
              <p className="text-xs text-red-500 mt-1" data-testid="password-mismatch-warning">
                {t('كلمتا المرور غير متطابقتين', 'Passwords do not match', 'Les mots de passe ne correspondent pas')}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="location">{t('الموقع (اختياري)', 'Location (Optional)', 'Localisation (Optionnel)')}</Label>
            <Input id="location" name="location" type="text" value={formData.location} onChange={handleChange}
              placeholder={t('أدخل موقعك', 'Enter your location', 'Entrez votre localisation')}
              data-testid="location-input" className="mt-2"
            />
          </div>

          <div className="bg-blue-50/70 border border-blue-100 rounded-xl p-3 flex items-start gap-3">
            <Checkbox id="agree-terms" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)}
              data-testid="agree-terms-checkbox"
              className="mt-0.5 border-[#1565c0] data-[state=checked]:bg-[#1565c0]"
            />
            <Label htmlFor="agree-terms" className="text-sm leading-relaxed text-gray-700 cursor-pointer">
              {t('أوافق على ', 'I agree to the ', "J'accepte les ")}
              <a href={getLegalDocUrl('terms')} className="text-[#1565c0] font-semibold underline underline-offset-2 hover:text-[#0d47a1]"
                data-testid="terms-link">
                {t('الشروط والأحكام', 'Terms & Conditions', 'Conditions générales')}
              </a>
              {t(' و', ' and ', ' et ')}
              <a href={getLegalDocUrl('privacy')} className="text-[#1565c0] font-semibold underline underline-offset-2 hover:text-[#0d47a1]"
                data-testid="privacy-link">
                {t('سياسة الخصوصية', 'Privacy Policy', 'Politique de confidentialité')}
              </a>.
            </Label>
          </div>

          <Button type="submit"
            className="w-full bg-[#1565c0] hover:bg-[#0d47a1] h-11 rounded-xl font-bold disabled:opacity-60"
            disabled={loading || !agreed}
            data-testid="register-submit-button">
            {loading
              ? t('جاري التسجيل...', 'Registering...', 'Inscription...')
              : t('إنشاء حساب', 'Create Account', 'Créer un compte')}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            {t('لديك حساب بالفعل؟', 'Already have an account?', 'Vous avez déjà un compte ?')}{' '}
            <Link to="/login" className="text-[#1565c0] hover:underline font-semibold" data-testid="login-link">
              {t('تسجيل الدخول', 'Login', 'Connexion')}
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
};
